import React from 'react';

// GOV13 base-plan polish — presentation-only BASE-PLAN subscription invoices (recurring base-plan billing ONLY).
// It receives only the base-plan invoice list — one-time purchase receipts can never appear here. An empty state
// is truthful and NEVER implies the current plan is invalid. Download appears only when the backend marks a
// document available (never a broken link / raw object path). No fetch.
const money = (amt, cur = 'usd') =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: (cur || 'usd').toUpperCase() }).format(amt || 0);

export default function BasePlanInvoicesTable({ invoices = [], hasCommercialReceipts = false, onDownload,
                                               downloadingKey = null, t = (k) => k }) {
  if ((invoices || []).length === 0) {
    return (
      <div className="text-center py-10 text-gray-500" data-testid="invoices-empty">
        <div>No base-plan subscription invoices are available.</div>
        {hasCommercialReceipts && (
          <div className="text-xs text-gray-400 mt-1">Your paid add-on and pack receipts are available above under Purchase receipts.</div>
        )}
      </div>
    );
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" data-testid="invoices-table">
        <thead className="text-xs text-gray-500 uppercase border-b">
          <tr>{['Invoice', 'Billing period', 'Subtotal', 'Tax', 'Total', 'Status', 'Receipt'].map((h) => (
            <th key={h} className="text-left py-2 px-2">{h}</th>))}</tr>
        </thead>
        <tbody>
          {invoices.map((inv) => (
            <tr key={inv.invoice_number} className="border-b last:border-0" data-testid={`base-invoice-${inv.invoice_number}`}>
              <td className="py-2 px-2 font-mono text-xs">
                <div>{inv.invoice_number}</div>
                {inv.plan_display_name && (
                  <div className="text-[10px] text-gray-500 font-sans" data-testid={`base-invoice-${inv.invoice_number}-plan`}>
                    {inv.plan_display_name}{inv.plan_version ? ` ${inv.plan_version}` : ''}
                  </div>
                )}
              </td>
              <td className="py-2 px-2">
                {inv.period_start ? `${String(inv.period_start).slice(0, 10)} – ${String(inv.period_end || '').slice(0, 10)}` : String(inv.issued_at || '').slice(0, 10)}
              </td>
              <td className="py-2 px-2">{inv.subtotal != null ? money(inv.subtotal, inv.currency) : '—'}</td>
              {/* Truthful tax: a historical charge with no GST snapshot shows "—" (not itemized), never a fabricated tax. */}
              <td className="py-2 px-2">{inv.tax_status === 'NOT_CHARGED' ? '—' : (inv.tax_amount != null ? money(inv.tax_amount, inv.currency) : '—')}</td>
              <td className="py-2 px-2 font-medium">{money(inv.total != null ? inv.total : inv.amount, inv.currency)}</td>
              <td className="py-2 px-2">
                <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                  inv.status === 'paid' ? 'bg-green-100 text-green-700' : inv.refunded ? 'bg-purple-100 text-purple-700' : 'bg-amber-100 text-amber-700'}`}>
                  {inv.status}
                </span>
              </td>
              <td className="py-2 px-2">
                {inv.document_available ? (
                  <button type="button" disabled={downloadingKey === inv.invoice_number} onClick={() => onDownload && onDownload(inv)}
                    data-testid={`download-base-invoice-${inv.invoice_number}-btn`}
                    className="px-2 py-1 text-xs border rounded hover:bg-gray-50 disabled:opacity-50">
                    {downloadingKey === inv.invoice_number ? 'Downloading…' : 'Download receipt'}
                  </button>
                ) : <span className="text-xs text-gray-400">—</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
